import { FooterIconProperty } from '../config'
import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { footerGlyphs } from './glyphs'
import { observer } from 'mobx-react-lite'
import { trainingConstructorStore } from '@pages/TrainingConstructor/stores'
import { useLocation } from 'react-router-dom'
import { ROUTES } from '@shared/constants/routes'
import './footer-badge.css'

interface FooterIconProps {
	iconInfo: FooterIconProperty
	isActive: boolean
	isCurrent: boolean
	onSelect: () => void
}

export const FooterIcon: FC<FooterIconProps> = observer(({
	iconInfo,
	isActive,
	isCurrent,
	onSelect,
}) => {
	const { t } = useTranslation('common')
	const location = useLocation()
	const Icon = footerGlyphs[iconInfo.icon]
	const title = t(iconInfo.titleKey)

	const shouldShowPendingBadge =
		iconInfo.icon === 'workouts' &&
		location.pathname !== ROUTES.overall.trainingConstructor

	const pendingExercisesCount = shouldShowPendingBadge
		? trainingConstructorStore.addedExercises.length
		: 0

	const [renderedBadgeCount, setRenderedBadgeCount] = useState(pendingExercisesCount)
	const [isBadgeVisible, setIsBadgeVisible] = useState(pendingExercisesCount > 0)
	const hideTimerRef = useRef<number | null>(null)

	useEffect(() => {
		if (hideTimerRef.current) {
			window.clearTimeout(hideTimerRef.current)
			hideTimerRef.current = null
		}

		if (pendingExercisesCount > 0) {
			setRenderedBadgeCount(pendingExercisesCount)
			setIsBadgeVisible(true)
			return
		}

		setIsBadgeVisible(false)
		hideTimerRef.current = window.setTimeout(() => {
			setRenderedBadgeCount(0)
			hideTimerRef.current = null
		}, 190)

		return () => {
			if (hideTimerRef.current) {
				window.clearTimeout(hideTimerRef.current)
				hideTimerRef.current = null
			}
		}
	}, [pendingExercisesCount])

	const buttonClassName = [
		'group relative z-10 flex h-[58px] shrink-0 items-center justify-center overflow-visible rounded-[23px] transition-[width,background-color,box-shadow,transform,color] [transition-duration:260ms] [transition-timing-function:cubic-bezier(0.32,0.72,0,1)] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B9EA25]/45 motion-reduce:transition-none',
		isActive
			? 'w-[140px] bg-[#F7F8F3]/[0.96] text-[#111315] shadow-[0_8px_18px_rgba(17,19,22,0.045),inset_0_0_0_1px_rgba(22,24,28,0.035),inset_0_1px_0_rgba(255,255,255,0.86)]'
			: 'w-[60px] bg-white/[0.34] text-[#747B82] shadow-[inset_0_0_0_1px_rgba(22,24,28,0.035),inset_0_1px_0_rgba(255,255,255,0.68)]',
	].join(' ')

	const labelClassName = [
		'overflow-hidden whitespace-nowrap text-[15px] font-semibold leading-none transition-[max-width,opacity,transform,color,margin-left] [transition-duration:230ms] [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none',
		isActive
			? 'ml-[10px] max-w-[92px] translate-x-0 opacity-100 text-[#17191B]'
			: 'max-w-0 -translate-x-1 opacity-0 text-[#747B82]',
	].join(' ')

	return (
		<button
			type="button"
			aria-label={title}
			aria-current={isCurrent ? 'page' : undefined}
			data-active={isActive}
			className={buttonClassName}
			onClick={onSelect}
		>
			<Icon className="size-[23px] shrink-0 opacity-[0.84] transition-[color,opacity,transform] [transition-duration:230ms] [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] group-hover:text-[#2E3337] group-data-[active=true]:scale-[1.05] group-data-[active=true]:text-[#111315] group-data-[active=true]:opacity-100 motion-reduce:transition-none" />

			{renderedBadgeCount > 0 && (
				<span
					aria-label={`${renderedBadgeCount} выбранных упражнений`}
					data-visible={isBadgeVisible}
					className="footer-workouts-badge absolute right-[2px] top-[2px] flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[#85D600] px-[4px] text-[10px] font-bold leading-none text-white shadow-[0_2px_6px_rgba(133,214,0,0.36)]"
				>
					{renderedBadgeCount}
				</span>
			)}

			<span aria-hidden={!isActive} className={labelClassName}>
				{title}
			</span>
		</button>
	)
})
